import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Plus, Users } from "lucide-react";

// Public page: NO auth. Reads/writes via plain axios (not the api client
// that attaches auth cookies) so there's no interceptor confusion if an
// employee accidentally lands here while logged into a different account.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  { value: "peer", label: "Peer", help: "Someone at a similar level who works alongside you" },
  { value: "direct_report", label: "Direct report", help: "Someone who reports to you" },
  { value: "cross_functional", label: "Cross-functional partner", help: "A collaborator from another team or discipline" },
];

const emptyNominee = () => ({ name: "", email: "", role_type: "peer", relationship_note: "" });

export default function Nominate360() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nominees, setNominees] = useState([emptyNominee(), emptyNominee(), emptyNominee()]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/360/nominate/${token}`)
      .then((r) => {
        setCtx(r.data);
        if (r.data.readonly) setSubmitted(true);
      })
      .catch((e) => setError(e?.response?.data?.detail || "This link isn't valid or has expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const peerCount = useMemo(() => nominees.filter((n) => n.role_type === "peer" && n.email.trim()).length, [nominees]);
  const totalCount = useMemo(() => nominees.filter((n) => n.email.trim()).length, [nominees]);

  const addRow = () => {
    if (!ctx) return;
    if (nominees.length >= ctx.max_total_raters) {
      toast.error(`You can nominate at most ${ctx.max_total_raters} raters.`);
      return;
    }
    setNominees((rows) => [...rows, emptyNominee()]);
  };
  const removeRow = (i) => setNominees((rows) => rows.filter((_, idx) => idx !== i));
  const updateRow = (i, k, v) => setNominees((rows) => rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  const submit = async (e) => {
    e.preventDefault();
    if (!ctx) return;
    const clean = nominees
      .map((n) => ({ ...n, email: n.email.trim(), name: n.name.trim() }))
      .filter((n) => n.email && n.name);
    if (clean.length < ctx.min_peers) {
      toast.error(`Please nominate at least ${ctx.min_peers} people.`);
      return;
    }
    if (clean.length > ctx.max_total_raters) {
      toast.error(`At most ${ctx.max_total_raters} nominees allowed.`);
      return;
    }
    // Client-side peer-count check mirrors backend; manager can still add
    // raters later, but the employee must meet the minimum themselves.
    const peers = clean.filter((n) => n.role_type === "peer").length;
    if (peers < ctx.min_peers) {
      toast.error(`At least ${ctx.min_peers} peers required (currently ${peers}).`);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/360/nominate/${token}`, { nominees: clean });
      toast.success("Thanks — your nominations are with your manager for approval.");
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="max-w-xl mx-auto px-6 py-24 text-center font-mono-cal text-xs" data-testid="nominate-loading">[LOADING…]</div>;
  }
  if (error) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="nominate-error">
        <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-2">[LINK EXPIRED]</div>
        <h1 className="font-display font-bold text-3xl mb-4">We couldn't open this link</h1>
        <p className="text-[#666] mb-6">{error}</p>
        <Link to="/" className="cal-btn">Go home</Link>
      </div>
    );
  }

  if (submitted || ctx.readonly) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="nominate-done">
        <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#00A36C] mb-2">[✓ NOMINATIONS RECEIVED]</div>
        <h1 className="font-display font-bold text-3xl mb-3">Thanks, {ctx.employee_name?.split(" ")[0] || "there"}!</h1>
        <p className="text-[#666] mb-6">
          Your proposed raters are now with <strong>{ctx.manager_name}</strong> for approval. You'll get an email once the review window opens.
        </p>
        <div className="cal-card p-4 text-left bg-[#FAFAF5]">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-2">[ WHAT HAPPENS NEXT ]</div>
          <ol className="text-sm space-y-1 list-decimal list-inside text-[#333]">
            <li>Your manager reviews the list (approve / swap / add required raters)</li>
            <li>Each approved rater receives a secure, single-use link</li>
            <li>Once enough raters have submitted, you'll receive the synthesised feedback</li>
          </ol>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="nominate-page">
      <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#FF4F00] mb-3">[ 360° REVIEW · YOUR NOMINATIONS ]</div>
      <h1 className="font-display font-bold text-4xl mb-2">Who should rate you?</h1>
      <p className="text-[#333] mb-6">
        <strong>{ctx.manager_name}</strong> has started a 360° review covering {ctx.period_label?.toLowerCase() || "the past period"}. Please nominate the people who've worked closely enough with you to give meaningful feedback.
      </p>

      <div className="cal-card p-4 mb-6 bg-[#FAFAF5]" data-testid="nominate-rules">
        <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-2">[ GUIDELINES ]</div>
        <ul className="text-sm space-y-1 text-[#333]">
          <li>• Nominate between <strong>{ctx.min_peers}</strong> and <strong>{ctx.max_total_raters}</strong> people</li>
          <li>• At least <strong>{ctx.min_peers} peers</strong> (people at your level) — this preserves everyone's anonymity</li>
          <li>• Direct reports and cross-functional partners strongly welcomed if relevant</li>
          <li>• Your manager will review this list and may approve, swap, or add required raters</li>
          <li>• Nobody is contacted until your manager approves</li>
        </ul>
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="nominate-form">
        {nominees.map((n, i) => (
          <div key={i} className="cal-card p-4" data-testid={`nominee-row-${i}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">[ RATER {i + 1} ]</div>
              {nominees.length > ctx.min_peers && (
                <button type="button" onClick={() => removeRow(i)} className="text-[#B00020] p-1" data-testid={`nominee-remove-${i}`} aria-label="Remove">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="cal-label">Full name</label>
                <input required value={n.name} onChange={(e) => updateRow(i, "name", e.target.value)} className="cal-input" data-testid={`nominee-name-${i}`}/>
              </div>
              <div>
                <label className="cal-label">Email</label>
                <input required type="email" value={n.email} onChange={(e) => updateRow(i, "email", e.target.value)} className="cal-input" data-testid={`nominee-email-${i}`}/>
              </div>
            </div>
            <div className="mt-3">
              <label className="cal-label">Their relationship to you</label>
              <div className="grid grid-cols-3 gap-2">
                {ROLE_OPTIONS.map((r) => (
                  <button key={r.value} type="button" onClick={() => updateRow(i, "role_type", r.value)}
                    className={`p-2 border border-[#111] text-left transition ${n.role_type === r.value ? "bg-[#FF4F00] text-white" : "bg-white hover:bg-[#F4F4F0]"}`}
                    data-testid={`nominee-role-${i}-${r.value}`}>
                    <div className="font-display font-bold text-xs">{r.label}</div>
                    <div className={`text-[10px] mt-0.5 ${n.role_type === r.value ? "text-white/80" : "text-[#666]"}`}>{r.help}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-3">
              <label className="cal-label">Context (optional)</label>
              <input value={n.relationship_note} onChange={(e) => updateRow(i, "relationship_note", e.target.value)}
                placeholder="e.g. We paired on the Q4 integration project"
                className="cal-input" data-testid={`nominee-note-${i}`}/>
            </div>
          </div>
        ))}

        <button type="button" onClick={addRow} disabled={nominees.length >= ctx.max_total_raters}
          className="cal-btn cal-btn-ghost w-full disabled:opacity-50" data-testid="nominee-add">
          <Plus size={14} className="inline mr-1" /> Add another rater ({nominees.length}/{ctx.max_total_raters})
        </button>

        <div className="cal-card p-4 flex items-center justify-between" data-testid="nominate-summary">
          <div className="text-sm">
            <strong className={peerCount >= ctx.min_peers ? "text-[#00A36C]" : "text-[#B00020]"}>{peerCount}</strong> peers · {totalCount} total
          </div>
          <button type="submit" disabled={submitting} className="cal-btn" data-testid="nominate-submit">
            {submitting ? "Submitting…" : <><Users size={14} /> Send to manager →</>}
          </button>
        </div>
      </form>
    </div>
  );
}
